import React, { useRef, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import getIpfsImageUrl from '@/utils/getIpfsImageUrl';
import { getTexture } from '@/utils/textureCache';
import { useTexture } from '@/hooks/useTextureCache';

const CITY_ASPECT = 50 / 35;
const BASE_SIZE = 80;
const BRAND_GREEN = '#84c341';

function CityMesh({ city, width, height, dimmed, isSelected, onClick, onHover, texture, cameraZoom }) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const cityName = city.details?.name || city.params?.name || `City #${city.index}`;

  useFrame(() => {
    if (!meshRef.current) return;
    const targetScale = hovered || isSelected ? 1.12 : 1;
    const currentScale = meshRef.current.scale.x;
    meshRef.current.scale.setScalar(THREE.MathUtils.lerp(currentScale, targetScale, 0.12));
  });

  return (
    <>
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(city);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          onHover?.(city, true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          onHover?.(city, false);
          document.body.style.cursor = 'default';
        }}
      >
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial
          map={texture}
          color={texture ? '#ffffff' : '#374151'}
          transparent
          opacity={dimmed ? 0.2 : 1}
        />
      </mesh>

      {isSelected && (
        <mesh position={[0, 0, -0.1]}>
          <planeGeometry args={[width + 8, height + 8]} />
          <meshBasicMaterial color={BRAND_GREEN} />
        </mesh>
      )}

      {hovered && cameraZoom > 0.5 && (
        <Html position={[0, height / 2 + 10, 0]} center style={{ pointerEvents: 'none' }}>
          <div className="bg-gray-900/95 px-3 py-1.5 rounded-lg text-white text-sm whitespace-nowrap shadow-xl border border-primary-500/30">
            <span className="font-medium">{cityName}</span>
            <span className="text-gray-500 ml-2">#{city.index}</span>
          </div>
        </Html>
      )}
    </>
  );
}

export default function ShittyMapCity({
  city,
  position,
  isSelected,
  isHighlighted,
  isVisible,
  onClick,
  onHover,
  cameraZoom,
}) {
  const width = BASE_SIZE * CITY_ASPECT;
  const height = BASE_SIZE;
  const dimmed = isHighlighted === false;

  const imageUrl = useMemo(() => {
    if (!city.cid) return null;
    return getIpfsImageUrl(city.cid, { width: 400, quality: 80 });
  }, [city.cid]);

  const shouldLoad = isVisible || (imageUrl && getTexture(imageUrl) !== null);
  const { texture, loading } = useTexture(shouldLoad ? imageUrl : null);

  if (loading && imageUrl) {
    return (
      <group position={position}>
        <mesh>
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial color="#1f2937" transparent opacity={dimmed ? 0.2 : 0.5} />
        </mesh>
      </group>
    );
  }

  return (
    <group position={position}>
      <CityMesh
        city={city}
        width={width}
        height={height}
        dimmed={dimmed}
        isSelected={isSelected}
        onClick={onClick}
        onHover={onHover}
        texture={texture}
        cameraZoom={cameraZoom}
      />
    </group>
  );
}
